import React, { useEffect, useRef, useState } from 'react';
import { identityData } from '../data/mock';
import { Quote, Lightbulb, Globe, Wrench, PenTool } from 'lucide-react';

const IdentitySection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const valueIcons = [Lightbulb, Globe, Wrench];

  return (
    <section
      id="identity"
      ref={sectionRef}
      className="min-h-screen px-6 py-20 md:pl-24"
    >
      <div className="max-w-6xl mx-auto space-y-16">
        
        {/* Notre Credo */}
        <div 
          className={`bg-[#FDFBF7]/95 border-[3px] border-[#2D2D2D] p-8 md:p-10
            transition-all duration-700
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
          `}
          style={{ 
            borderRadius: '15px 225px 15px 255px / 255px 15px 225px 15px',
            boxShadow: '5px 5px 0px #2D2D2D'
          }}
        >
          <div className="flex items-start gap-4 mb-4">
            <Quote className="w-8 h-8 text-[#F08A24] flex-shrink-0 icon-sketch" />
            <h2 className="font-marker text-2xl md:text-3xl text-[#1F4E79]">
              {identityData.credo.title}
            </h2>
          </div>
          <p className="font-architect text-lg md:text-xl text-[#2D2D2D]/90 leading-relaxed pl-12">
            {identityData.credo.text}
          </p>
        </div>

        {/* Qui sommes-nous - Two columns */}
        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          {/* Left - Text content */}
          <div 
            className={`bg-[#FDFBF7]/95 border-[3px] border-[#2D2D2D] p-8
              transition-all duration-700 delay-200
              ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}
            `}
            style={{ 
              borderRadius: '225px 15px 255px 15px / 15px 255px 15px 225px',
              boxShadow: '5px 5px 0px #2D2D2D'
            }}
          >
            <h3 className="font-marker text-2xl md:text-3xl text-[#2D2D2D] mb-2">
              {identityData.about.title}
            </h3>
            <h4 className="font-patrick text-xl text-[#F08A24] mb-6">
              {identityData.about.subtitle}
            </h4>
            <p className="font-architect text-base md:text-lg text-[#2D2D2D]/85 leading-relaxed mb-6">
              {identityData.about.text}
            </p>
            <div 
              className="bg-[#1F4E79] text-white p-4 font-patrick text-lg"
              style={{ 
                borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px',
                boxShadow: '3px 3px 0px #2D2D2D'
              }}
            >
              {identityData.about.tagline}
            </div>
          </div>

          {/* Right - Scribing visual */}
          <div 
            className={`min-h-[300px] flex items-center justify-center
              transition-all duration-700 delay-300
              ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}
            `}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_paper-agility/artifacts/o47n6ymu_Gemini_Generated_Image_ul28mhul28mhul28%20%281%29_upscayl_2x_digital-art-4x.png"
              alt="BAIM Expertise - Tech, Produit, Humain, Stratégie"
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Values cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {identityData.values.map((value, index) => {
            const IconComponent = valueIcons[index];
            return (
              <div
                key={index}
                className={`bg-[#FDFBF7]/95 border-[3px] border-[#2D2D2D] p-6
                  transition-all duration-500 hover:scale-102 cursor-default
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                `}
                style={{ 
                  borderRadius: index === 0 ? '255px 15px 225px 15px / 15px 225px 15px 255px' :
                                index === 1 ? '15px 225px 15px 255px / 255px 15px 225px 15px' :
                                '225px 15px 255px 15px / 15px 255px 15px 225px',
                  boxShadow: '4px 4px 0px #2D2D2D',
                  animationDelay: `${0.4 + index * 0.15}s`,
                  transitionDelay: `${0.4 + index * 0.15}s`
                }}
              >
                <IconComponent className="w-10 h-10 text-[#F08A24] mb-4 icon-sketch" />
                <h4 className="font-patrick text-xl text-[#1F4E79] mb-3">
                  {value.title}
                </h4>
                <p className="font-architect text-base text-[#2D2D2D]/80 leading-relaxed">
                  {value.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* Visual approach section */}
        <div 
          className={`bg-[#F08A24]/10 border-[3px] border-[#F08A24] p-8
            transition-all duration-700 delay-500
            ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
          `}
          style={{ 
            borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px',
            boxShadow: '5px 5px 0px #F08A24'
          }}
        >
          <div className="flex items-start gap-4 mb-4">
            <PenTool className="w-8 h-8 text-[#F08A24] flex-shrink-0 icon-sketch" />
            <h3 className="font-marker text-xl md:text-2xl text-[#2D2D2D]">
              {identityData.visual.question}
            </h3>
          </div>
          <p className="font-architect text-lg text-[#2D2D2D]/85 leading-relaxed mb-6 pl-12">
            {identityData.visual.text}
          </p>
          <div className="pl-12">
            <span 
              className="inline-block bg-[#1F4E79] text-white px-6 py-3 font-patrick text-lg"
              style={{ 
                borderRadius: '15px 225px 15px 255px / 255px 15px 225px 15px',
                boxShadow: '3px 3px 0px #2D2D2D'
              }}
            >
              {identityData.visual.slogan}
            </span>
          </div>
        </div>

      </div>
    </section>
  );
};

export default IdentitySection;
